import { AllFacets, Entry, PlpProduct } from '@/types/plp'
import { AnalyticsPluginD } from '@/scripts/libs/vue-multianalytics/declarations'

export type AlgoliaFacets = Record<string, Record<string, number>>

export interface PriceRange {
  min: string | number
  max: string | number
}

export interface AlgoliaSuggestion {
  nb_words: number
  objectId: string
  popularity: number
  query: string
}

export interface AlgoliaProductHit {
  attachments: Array<Attachment>
  attributes_translated: Attributes
  attributes: Attributes
  remixableUrl?: string
  businessRank: { ungroupedSequence: number }
  buyable: boolean
  catalogId: number
  catchall: Array<string>
  categories_translated: Array<string>
  categories_tree_translated: Record<string, Array<string>>
  categories_tree: Record<string, Array<string>>
  categories: Array<string>
  category_ids: Array<string>
  display: boolean
  inventoryQuantity: number
  languageId: number
  languageLocale: string
  lastUpdate: string
  lifecycle: boolean
  longDescription: string
  massoc: Record<string, Array<string>>
  media: { defaultImage2: string; defaultImage1: string }
  newLabel: any // TODO: change property name when response from algolia will be available
  objectID: string
  parentPartnumberId: string
  parentProductId: number
  partnumberId: string
  preorderText: any // TODO: change property name when response from algolia will be available
  prices: AlgoliaPrices
  productId: number
  segments: Array<unknown>
  storeId: number
  url: string
  variants?: Array<AlgoliaProductHit>
  x_groupkey: string
}

export interface Attachment {
  id: string
  identifier: string
  rule: string
  sequence: number
  store: string
  url: string
  name: PlpAttachmentsNameE
}

type AlgoliaPrices = { standard: AlgoliaPrice; [key: string]: AlgoliaPrice }

export interface AlgoliaPrice {
  listPrice: number
  offerPrice: number
  currency: string
  startDate: string
  endDate: string
  pricelist: string
  priceListPrecedence: number
  segment?: string
  percentageDiscount: number
  amountOfDiscount: number
  badge?: string | undefined
  toggle?: string
  precedence: number
  futurePrices?: Array<AlgoliaPrice>
}

// TODO: merge with attributes from new index
type Attributes = Partial<{
  'RECOMMENDEDUSE/PERSONA': string
  AverageOverallRating: string
  BRAND: string
  BRIDGE: string
  CLEN: string
  COLLECTION: string
  COLLECTIONNAME: string
  COLLECTIONTYPE: string
  COLOR_CODE: string
  CUSTOMIZABLE?: string
  DISPLAYSKU: string
  DRIVING_ASNZS: string
  EXCLUSIVE: string
  FINISHING: string
  FISCAL_TAX_ID: string
  FRAMEFIT: string
  FRAMEMATERIAL: string
  FRAMEMATERIALFACET: string
  FRAMEMATERIALPERCEIVED: string
  FRAMESHAPEDESCRIPTION: string
  FRAMESHAPEFACET: string
  FRAMETYPE: string
  FRONT_COLOR_FACET: string
  FRONT_COLOR: string
  FRONT_INNER_COLOR_FACET: string
  FRONT_INNER_COLOR: string
  GENDER: string
  GEOFIT: string
  GRID: string
  HINGE_DISTANCE: string
  INCLUDED_ITEMS: string
  INCLUDED_SERVICES: string
  IS_ASIAN: string
  ISBASELENS: string
  LENS_COLOR_FACET: string
  LENS_COLOR: string
  LENS_CURVE: string
  LENS_HEIGHT: string
  LENS_TREATMENT_FACET: string
  LENS_TREATMENT: string
  LENSCOLORFAMILY: string
  LENSMATERIAL: string
  LENSWIDTH: string
  LIGHT_CONDITIONS: string
  LIMITED_EDITION: string
  MADEIN: string
  MARKETINGTHEMERANGE: string
  MATERIALTYPE: string
  ML_FRAME_BRIDGE_POSITION: string
  ML_FRAME_COLOR_PALETTE: string
  ML_FRAME_COLOR: string
  ML_FRAME_FRONT_MATERIAL: string
  ML_FRAME_RIM: string
  ML_FRAME_SHAPE: string
  ML_FRAME_TEMPLE_FITTING: string
  ML_FRAME_TEMPLE_THICKNESS: string
  ML_GLASS_GENDER: string
  ML_PRODUCT_TYPE: string
  MODEL_CODE_DISPLAY: string
  MODEL_NAME: string | Array<string>
  MODEL_SIZE_DISPLAY: string
  MODEL_SIZE: string
  MODELFAMILY: string
  MOST_SOLD_SIZE: string
  NEWRELEASES: string
  NOSEPAD_ADJUSTABLE: string
  NOSEPAD: string
  NOTAINFORMATIVA: string
  ONSALE?: string
  OverallRatingRange: string
  PARENT_NAME: string
  PHOTOCHROMIC: string
  POLARIZED: string
  POWER_COMBINED_MAX: string
  POWER_COMBINED_MIN: string
  PREDOMINANTCOLORFACET: string
  PREORDER?: string
  PRESCRIPTION_AVAILABLE: string
  PRODHIERARCHY0: string
  PRODHIERARCHY1: string
  PRODHIERARCHY2: string
  PRODUCT_TYPE: string
  PRODUCTTYPE: string
  PROGRESSIVE_FRIENDLY: string
  RatingsOnlyReviewCount: string
  REMIXABLE?: string
  ROXABLE: string
  SEG_HEIGHT: string
  SEQUENCE: string
  SKU_TYPE: string
  SKU: string
  SUSTAINABILITY_CLAIM: string
  TAX_ID: string
  TEMPLE_COLOR_FACET: string
  TEMPLE_COLOR: string
  TEMPLE_INNER_COLOR_FACET: string
  TEMPLE_INNER_COLOR: string
  TEMPLELENGTH: string
  VIRTUALMIRROR?: string
  VTO_ENABLED: string
  WIDTH: string
  AVANTPREMIER: string
  AVANTPREMIER_DATE: string
  NEWRELEASESMODEL: string
}>

export enum StatusMessageTypeE {
  PREORDER = 'preorder',
  UNAVAILABLE = 'unavailable',
  SOLDOUT = 'sold-out',
  NEW = 'new',
  LIMITED = 'limited',
  PRERELEASE = 'pre-release',
  COMINGSOON = 'coming-soon',
  WEBEXCLUSIVE = 'web-exclusive',
  WEBEXCLUSIVEJP = 'web-exclusive-jp',
}

export enum PlpAttachmentsNameE {
  SHAD_QT = 'Quarter',
  SHAD_LT = 'Lateral',
  SHAD_BK = 'Back',
  SHAD_CFR = 'Closed Front',
  ON_MODEL = 'On model',
}

export enum PlpAttachmentsRuleE {
  PDP_CAROUSEL = 'PDP Carousel',
  PLP = 'PLP',
}

export interface AnalyticsFacetD extends Entry {
  facetIdentifier: string
  min?: number
  max?: number
  mainLabel?: string
  PSTIdentifier?: string
}

export interface AlgoliaPlpTrackD {
  trackLoadingReady(products: Array<PlpProduct>, analyticsPlugin: AnalyticsPluginD): void
  getSelectedFacet(
    entry: Array<Entry>,
    facetKey: string,
    filterApplied: string,
    key: string,
    identifier: string
  ): Entry | undefined
  formatPriceForAnalytics(price: any): string
  getPriceFacet(entry: Array<Entry>): AnalyticsFacetD
  appliedFilterToEntries(
    filterApplied: Array<string> | undefined,
    facets: AllFacets | undefined
  ): Array<AnalyticsFacetD | undefined>
  buildAppliedFacetsAnalytics(
    mappedFacetIdentifier?: Array<string>,
    appliedOrderBy?: string
  ): string | undefined
  mapFacetIdentifiers(
    appliedFilterToEntries: Array<AnalyticsFacetD>,
    currencySymbol: string
  ): Array<string>
  getAttributeFromKey(key: string): string | undefined
  trackSearchEvent(
    totalResults: number,
    filterApplied: Array<string>,
    facets: AllFacets,
    analyticsPlugin: AnalyticsPluginD
  ): void
}

export type FacetStatsRecordD = Record<
  string,
  { min: number; max: number; avg: number; sum: number }
>
